const SKELETON_COUNT = 4;
const SKELETON_MARKUP =
  '<li class="wld-pcard-skeleton-item" aria-hidden="true">' +
  '<div class="wld-pcard wld-pcard--skeleton">' +
  '<span class="wld-pcard__img"></span>' +
  '<div class="wld-pcard__pi">' +
  '<span class="wld-pcard__line wld-pcard__line--brand"></span>' +
  '<span class="wld-pcard__line wld-pcard__line--name"></span>' +
  '<span class="wld-pcard__line wld-pcard__line--name2"></span>' +
  '<span class="wld-pcard__line wld-pcard__line--price"></span>' +
  '</div></div></li>';

class WldCollectionPagination extends HTMLElement {
  connectedCallback() {
    this.grid = document.getElementById(this.dataset.gridId);
    this.link = this.querySelector('[data-wld-load-more]');
    this.sentinel = this.querySelector('[data-wld-auto-load]');
    this.status = this.querySelector('[data-wld-load-status]');
    this.hasAutoLoaded = false;
    this.isLoading = false;

    if (!this.grid || !this.link) return;

    this.link.addEventListener('click', this.handleClick);
    this.grid.addEventListener('click', this.handleCardClick);

    // A back-navigation that misses the bfcache re-renders only the single page named in
    // the URL. Restore the earlier pages first so the grid matches what the user had
    // scrolled through, then let the browser's own scroll restoration (which we've
    // disabled below) get replaced by our own scroll-to-clicked-card.
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    this.backfillPreviousPages().then(() => {
      this.scrollToClickedCard();

      if (!this.sentinel || !('IntersectionObserver' in window)) {
        this.dataset.autoPending = 'false';
        return;
      }

      this.observer = new IntersectionObserver(this.handleIntersection, {
        rootMargin: '300px 0px',
      });
      this.observer.observe(this.sentinel);
    });
  }

  disconnectedCallback() {
    this.link?.removeEventListener('click', this.handleClick);
    this.grid?.removeEventListener('click', this.handleCardClick);
    this.observer?.disconnect();
  }

  /**
   * Stamp the clicked product card's page number onto the URL (as ?page=N) and its id
   * onto the hash before navigating away, so a later back-navigation knows how many
   * pages to restore and which card to scroll back to.
   */
  handleCardClick = (event) => {
    const link = event.target.closest ? event.target.closest('a[href]') : null;
    if (!link) return;

    const card = link.closest('li[data-page]');
    if (!card) return;

    const url = new URL(window.location.href);
    url.searchParams.set('page', card.dataset.page);
    url.hash = card.id;
    history.replaceState('', '', url);
  };

  /**
   * If the page loaded directly on ?page=N (N > 1), fetch and append every earlier
   * page's products so the grid looks the same as it did before the user left.
   */
  async backfillPreviousPages() {
    const targetPage = Number(new URL(window.location.href).searchParams.get('page'));
    if (!targetPage || targetPage <= 1 || !this.grid) return;

    for (let page = 2; page <= targetPage; page++) {
      await this.appendPage(page);
    }
  }

  /**
   * @param {number} pageNumber
   */
  async appendPage(pageNumber) {
    if (!this.link?.href || !this.grid) return;

    const requestUrl = new URL(this.link.href);
    requestUrl.searchParams.set('page', pageNumber.toString());
    requestUrl.searchParams.set('section_id', this.dataset.sectionId);

    try {
      const response = await fetch(requestUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGrid = page.getElementById(this.dataset.gridId);
      const nextPagination = page.querySelector('wld-collection-pagination');
      const nextLink = nextPagination?.querySelector('[data-wld-load-more]');
      const newItems = nextGrid ? Array.from(nextGrid.children) : [];

      if (newItems.length === 0) return;

      const fragment = document.createDocumentFragment();
      newItems.forEach((item) => fragment.append(item));
      this.grid.append(fragment);
      // Freshly appended cards render with aria-pressed="false" — sync their
      // heart state against the wishlist now that they exist in the DOM.
      document.dispatchEvent(new CustomEvent('wld:wishlist-change'));

      if (nextLink) {
        this.link.href = nextLink.href;
      } else {
        this.link.remove();
        this.sentinel?.remove();
      }
    } catch (error) {
      // Leave the "load more" link/sentinel in place so the user can retry manually.
    }
  }

  scrollToClickedCard() {
    const hash = window.location.hash;
    if (!hash) return;

    const card = document.getElementById(hash.slice(1));
    if (!card) return;

    card.scrollIntoView({ behavior: 'instant', block: 'center' });
  }

  handleIntersection = (entries) => {
    if (this.hasAutoLoaded || !entries.some((entry) => entry.isIntersecting)) return;

    this.hasAutoLoaded = true;
    this.observer?.disconnect();
    this.loadNextPage(false);
  };

  handleClick = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();
    this.loadNextPage(true);
  };

  async loadNextPage(isManual) {
    if (this.isLoading || !this.link?.href || !this.grid) return;

    const nextPageUrl = this.link.href;
    this.isLoading = true;
    this.link.setAttribute('aria-disabled', 'true');
    this.setAttribute('aria-busy', 'true');
    this.setStatus(this.dataset.loadingText);
    this.showSkeletons();

    try {
      const requestUrl = new URL(nextPageUrl);
      requestUrl.searchParams.set('section_id', this.dataset.sectionId);

      const response = await fetch(requestUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGrid = page.getElementById(this.dataset.gridId);
      const nextPagination = page.querySelector('wld-collection-pagination');
      const nextLink = nextPagination?.querySelector('[data-wld-load-more]');
      const newItems = nextGrid ? Array.from(nextGrid.children) : [];

      if (newItems.length === 0) throw new Error('No products found in response');

      this.removeSkeletons();
      const fragment = document.createDocumentFragment();
      newItems.forEach((item) => fragment.append(item));
      this.grid.append(fragment);
      // Freshly appended cards render with aria-pressed="false" — sync their
      // heart state against the wishlist now that they exist in the DOM.
      document.dispatchEvent(new CustomEvent('wld:wishlist-change'));

      const loadedPage = requestUrl.searchParams.get('page');
      if (loadedPage) {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('page', loadedPage);
        history.pushState('', '', currentUrl);
      }

      if (nextLink) {
        this.link.href = nextLink.href;
        this.link.removeAttribute('aria-disabled');
        this.dataset.autoPending = 'false';
      } else {
        this.link.remove();
        this.sentinel?.remove();
      }

      if (this.isConnected) {
        const visibleProducts = this.grid.children.length;
        const totalProducts = this.dataset.totalProducts || String(visibleProducts);
        const statusText = this.dataset.loadedText
          .replace('__COUNT__', String(visibleProducts))
          .replace('__TOTAL__', totalProducts);

        this.setStatus(statusText);
      }
    } catch (error) {
      this.removeSkeletons();

      if (isManual) {
        window.location.assign(nextPageUrl);
        return;
      }

      this.dataset.autoPending = 'false';
      this.link.removeAttribute('aria-disabled');
      this.setStatus(this.dataset.errorText);
    } finally {
      this.isLoading = false;
      this.removeAttribute('aria-busy');
    }
  }

  showSkeletons() {
    if (!this.grid) return;
    const fragment = document.createDocumentFragment();
    for (let i = 0; i < SKELETON_COUNT; i++) {
      fragment.append(document.createRange().createContextualFragment(SKELETON_MARKUP));
    }
    this.grid.append(fragment);
  }

  removeSkeletons() {
    this.grid?.querySelectorAll('.wld-pcard-skeleton-item').forEach((node) => node.remove());
  }

  setStatus(message) {
    if (this.status) this.status.textContent = message || '';
  }
}

if (!customElements.get('wld-collection-pagination')) {
  customElements.define('wld-collection-pagination', WldCollectionPagination);
}
